import { FC, useMemo, useState } from 'react';

import { SearchIcon, XIcon } from '@heroicons/react/outline';
import {
  GiftCouponRedeemStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

import {
  couponPhoneKindLabel,
  findPhoneGroups,
  FlatCouponEntry,
  flattenOrderCoupons,
  isInactiveCoupon,
  matchCouponEntry,
} from '../utils/couponFinder';
import {
  fmtPhone,
  redeemStatusColor,
  redeemStatusLabel,
} from './couponDetail/constants';
import { formatDateTimeCompact } from '../utils/giftOrder.constants';

/**
 * 검색된 주문 전체의 쿠폰을 한 번에 찾는 패널.
 *
 * 주문자 번호로 주문을 찾은 뒤 "그 중 어느 쿠폰이 이 사람 것이냐"를 가려내는 응대가 잦은데,
 * 쿠폰은 주문별 상세 모달 안에만 있어 주문 수만큼 열어봐야 했다. 여기서 메모(수령인 이름)·
 * 신청자·번호 뒷자리·쿠폰코드로 주문을 가로질러 찾고, 번호가 겹치거나 한 자리만 다른
 * 수령인 묶음도 함께 짚어준다.
 */

type StatusFilter = 'all' | 'pending' | 'delivered' | 'inactive';

const statusFilterLabel: Record<StatusFilter, string> = {
  all: '전체',
  pending: '발송대기',
  delivered: '발송완료',
  inactive: '취소·폐기',
};

const matchStatusFilter = (c: GiftCouponSummary, filter: StatusFilter): boolean => {
  if (filter === 'all') return true;
  if (filter === 'inactive') return isInactiveCoupon(c);
  if (filter === 'delivered') return c.redeemStatus === GiftCouponRedeemStatus.Delivered;
  return (
    c.redeemStatus === GiftCouponRedeemStatus.Available ||
    c.redeemStatus === GiftCouponRedeemStatus.Requested ||
    c.redeemStatus === GiftCouponRedeemStatus.ReselectRequired ||
    // 발송보류도 아직 처리 전이라 '발송대기' 묶음에 들어간다 (안 넣으면 어느 필터에도 안 걸린다)
    c.redeemStatus === GiftCouponRedeemStatus.OnHold
  );
};

interface CouponFinderPanelProps {
  /** 검색된 주문 — 화면 표시 순서 그대로 (주문 라벨 번호가 여기서 나온다) */
  orders: GiftOrderForAdmin[];
  /** 쿠폰 상세 모달 열기 (해당 쿠폰 강조) */
  onOpenCoupon: (order: GiftOrderForAdmin, couponId: string) => void;
  /** 아래 주문 표의 해당 행으로 스크롤 */
  onFocusOrder: (orderId: string) => void;
  /** 좁은 폭(모달 안)에서는 컬럼을 줄인다 */
  compact?: boolean;
}

const CouponResultRow: FC<{
  entry: FlatCouponEntry;
  onOpenCoupon: (order: GiftOrderForAdmin, couponId: string) => void;
  onFocusOrder: (orderId: string) => void;
  compact?: boolean;
}> = ({ entry, onOpenCoupon, onFocusOrder, compact }) => {
  const { coupon: c, order } = entry;
  const inactive = isInactiveCoupon(c);
  return (
    <tr className={`align-top ${inactive ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}>
      <td className="whitespace-nowrap px-2 py-2">
        <button
          type="button"
          onClick={() => onFocusOrder(order._id)}
          className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-200"
          title="아래 주문 목록에서 이 주문 행으로 이동"
        >
          {entry.orderLabel}
        </button>
        {!compact && (
          <p className="mt-0.5 text-[10px] text-gray-400">
            {formatDateTimeCompact(order.paymentDetail.purchasedAt || order.createdAt)}
          </p>
        )}
      </td>
      <td className="whitespace-nowrap px-2 py-2">
        <button
          type="button"
          onClick={() => onOpenCoupon(order, c._id)}
          className="font-mono text-[11px] font-semibold text-indigo-600 hover:text-indigo-900 hover:underline"
          title="쿠폰 상세 열기 (이 쿠폰 강조)"
        >
          {entry.couponLabel}
        </button>
        <span
          className={`ml-1 inline-flex rounded px-1 text-[10px] font-semibold ${
            redeemStatusColor[c.redeemStatus]
          }`}
        >
          {redeemStatusLabel[c.redeemStatus]}
        </span>
      </td>
      {!compact && (
        <td className="px-2 py-2 text-[11px] text-gray-600">{entry.itemName || '-'}</td>
      )}
      <td className="px-2 py-2 text-[11px] text-gray-900">
        {c.memo ? (
          <span className="font-medium">{c.memo}</span>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
      <td className="whitespace-nowrap px-2 py-2 text-[11px] text-gray-600">
        {c.recipient?.name || <span className="text-gray-300">-</span>}
      </td>
      <td className="px-2 py-2 text-[11px] text-gray-600">
        {entry.phones.length === 0 ? (
          <span className="text-gray-300">-</span>
        ) : (
          <div className="flex flex-col gap-0.5">
            {entry.phones.map((p) => (
              <span key={`${p.kind}-${p.digits}`} className="whitespace-nowrap">
                <span className="mr-1 rounded bg-gray-100 px-1 text-[9px] text-gray-500">
                  {couponPhoneKindLabel[p.kind]}
                </span>
                {fmtPhone(p.digits)}
              </span>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
};

export const CouponFinderPanel: FC<CouponFinderPanelProps> = ({
  orders,
  onOpenCoupon,
  onFocusOrder,
  compact,
}) => {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showGroups, setShowGroups] = useState(false);

  const entries = useMemo(() => flattenOrderCoupons(orders), [orders]);
  const phoneGroups = useMemo(() => findPhoneGroups(entries), [entries]);

  const results = useMemo(
    () =>
      entries.filter(
        (e) => matchStatusFilter(e.coupon, statusFilter) && matchCouponEntry(e, query),
      ),
    [entries, statusFilter, query],
  );

  if (entries.length === 0) return null;

  const isSearching = query.trim().length > 0 || statusFilter !== 'all';
  const similarGroupCount = phoneGroups.filter((g) => g.kind === 'similar').length;

  return (
    <div className="mt-4 rounded-lg bg-white shadow">
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">쿠폰 찾기</h3>
            <p className="text-xs text-gray-500">
              검색된 주문 {orders.length}건의 쿠폰 {entries.length}장에서 메모(수령인)·
              신청자·번호 뒷자리·쿠폰코드로 찾습니다.
            </p>
          </div>
          {phoneGroups.length > 0 && (
            <button
              type="button"
              onClick={() => setShowGroups((prev) => !prev)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
                showGroups
                  ? 'border-amber-400 bg-amber-50 text-amber-800'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
              title="같은 번호로 여러 장 나갔거나, 한 자리만 다른 번호가 섞여 있는 묶음"
            >
              번호 겹침·유사 묶음 {phoneGroups.length}
              {similarGroupCount > 0 && (
                <span className="ml-1 rounded bg-amber-200 px-1 text-[10px] font-bold text-amber-900">
                  유사 {similarGroupCount}
                </span>
              )}
            </button>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <SearchIcon className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="메모(수령인 이름) · 신청자 · 번호 뒷자리 · 쿠폰코드"
              className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-8 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                title="검색어 지우기"
              >
                <XIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {(Object.keys(statusFilterLabel) as StatusFilter[]).map((option) => {
              const count = entries.filter((e) =>
                matchStatusFilter(e.coupon, option),
              ).length;
              const isActive = statusFilter === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => setStatusFilter(option)}
                  disabled={option !== 'all' && count === 0}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                    isActive
                      ? 'bg-indigo-600 text-white'
                      : option !== 'all' && count === 0
                      ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                      : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span>{statusFilterLabel[option]}</span>
                  <span className="text-[10px] opacity-80">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {showGroups && phoneGroups.length > 0 && (
        <div className="border-b border-amber-200 bg-amber-50/60 px-4 py-3">
          <p className="mb-2 text-xs font-semibold text-amber-900">
            번호가 겹치거나 한 자리만 다른 묶음 — 잘못 적힌 번호를 가려낼 때
          </p>
          <div className="flex flex-col gap-2">
            {phoneGroups.map((group) => (
              <div
                key={group.members.map((m) => m.digits).join('|')}
                className="rounded-md border border-amber-200 bg-white p-2"
              >
                <span
                  className={`mr-2 inline-flex rounded px-1.5 py-0.5 text-[10px] font-bold ${
                    group.kind === 'similar'
                      ? 'bg-amber-200 text-amber-900'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {group.kind === 'similar' ? '한 자리 차이' : '같은 번호'}
                </span>
                <span className="text-[11px] text-gray-500">
                  쿠폰 {group.couponCount}장
                </span>
                <div className="mt-1 flex flex-col gap-1">
                  {group.members.map((member) => (
                    <div key={member.digits} className="text-[11px]">
                      <button
                        type="button"
                        onClick={() => setQuery(member.digits)}
                        className="font-mono font-semibold text-indigo-600 hover:underline"
                        title="이 번호로 결과 필터"
                      >
                        {fmtPhone(member.digits)}
                      </button>
                      <span className="ml-2 text-gray-600">
                        {member.entries
                          .map(
                            (e) =>
                              `${e.orderLabel} ${e.couponLabel}${
                                e.coupon.memo ? ` (${e.coupon.memo})` : ''
                              }`,
                          )
                          .join(' · ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="max-h-96 overflow-auto">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 bg-gray-50">
            <tr className="text-left text-gray-500">
              <th className="whitespace-nowrap px-2 py-2 font-medium">주문</th>
              <th className="whitespace-nowrap px-2 py-2 font-medium">쿠폰</th>
              {!compact && (
                <th className="whitespace-nowrap px-2 py-2 font-medium">상품</th>
              )}
              <th className="whitespace-nowrap px-2 py-2 font-medium">메모</th>
              <th className="whitespace-nowrap px-2 py-2 font-medium">신청자</th>
              <th className="whitespace-nowrap px-2 py-2 font-medium">번호</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {results.length === 0 ? (
              <tr>
                <td
                  colSpan={compact ? 5 : 6}
                  className="px-2 py-6 text-center text-gray-500"
                >
                  조건에 맞는 쿠폰이 없습니다.
                </td>
              </tr>
            ) : (
              results.map((entry) => (
                <CouponResultRow
                  key={entry.coupon._id}
                  entry={entry}
                  onOpenCoupon={onOpenCoupon}
                  onFocusOrder={onFocusOrder}
                  compact={compact}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {isSearching && (
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-500">
          {results.length}건 / 전체 {entries.length}장
        </div>
      )}
    </div>
  );
};
